/**
 * HandoffNudgeCard — the family-facing "would you like a case manager?" card
 * raised by the silence, lifecycle and claim-stress signal evaluators.
 *
 * NOT RENDERED TODAY. #1801 put every family-facing entry point to the
 * in-thread handoff behind the server-read `HANDOFF_FAMILY_ENTRY_ENABLED`
 * flag, which is dark by default. Two gates, both driven by that one flag:
 * the three evaluators stop CREATING `awaiting_family` rows while dark, and
 * ChatPage's `pendingNudge` refuses to surface any row that already exists.
 * Nothing is deleted — flip the flag on and the cards come back whole.
 *
 * A tab that loaded the card before the flag went dark can still post, and
 * the server refuses with 503 `live_conversation_dormant` (#2567). The card
 * then hides itself: the conversation is back with the companion and there
 * is nothing left to answer. Any other failure keeps the card and its
 * buttons and shows one inline line, so the family can try again.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { useMsg, type TKey } from '@/i18n/msg';
import { serverCode } from '@/lib/serverMessage';

export type NudgeCategory = 'silence' | 'lifecycle' | 'claim_stress';

interface HandoffNudgeCardProps {
  handoffId: string;
  category: NudgeCategory;
  onResponded: (accepted: boolean) => void;
}

// #2109 R3-B4: a key map, resolved at render (chat:handoff.nudge.*).
const PROMPTS: Record<NudgeCategory, { title: TKey; body: TKey }> = {
  silence: {
    title: 'chat:handoff.nudge.silence.title',
    body: 'chat:handoff.nudge.silence.body',
  },
  lifecycle: {
    title: 'chat:handoff.nudge.lifecycle.title',
    body: 'chat:handoff.nudge.lifecycle.body',
  },
  // #657. "Case manager", not "TRFer" — the family-facing lexicon fixed by
  // #636–#639 ("one voice, one lexicon"); the wording proposed in the issue
  // predates that pass. Names no amount, no vendor and no claim: the card is
  // an offer of a person, not a status report the platform cannot stand behind.
  claim_stress: {
    title: 'chat:handoff.nudge.claimStress.title',
    body: 'chat:handoff.nudge.claimStress.body',
  },
};

export function HandoffNudgeCard({ handoffId, category, onResponded }: HandoffNudgeCardProps) {
  const { t } = useTranslation('chat');
  const resolve = useMsg();
  const [resolved, setResolved] = useState(false);
  const [failed, setFailed] = useState(false);

  // Nothing changed on the server for either failure, so onResponded (which
  // refetches the pending list) is not called.
  const onError = (err: unknown) => {
    if (serverCode(err) === 'live_conversation_dormant') {
      setResolved(true);
      return;
    }
    setFailed(true);
  };

  const accept = useMutation({
    mutationFn: () => api.handoffs.respondNudge(handoffId, true),
    onMutate: () => setFailed(false),
    onSuccess: () => {
      setResolved(true);
      onResponded(true);
    },
    onError,
  });

  const decline = useMutation({
    mutationFn: () => api.handoffs.respondNudge(handoffId, false),
    onMutate: () => setFailed(false),
    onSuccess: () => {
      setResolved(true);
      onResponded(false);
    },
    onError,
  });

  if (resolved) return null;

  const prompt = PROMPTS[category];
  const pending = accept.isPending || decline.isPending;

  return (
    <Card className="border-l-4 border-l-status-warn-fg my-3">
      <CardContent className="p-4">
        <h4 className="font-semibold text-body mb-1">{resolve({ key: prompt.title })}</h4>
        <p className="text-body text-stevens-ink-3 mb-3">{resolve({ key: prompt.body })}</p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-stevens-indigo-solid text-stevens-indigo-solid-fg hover:bg-stevens-indigo-solid-2"
            disabled={pending}
            onClick={() => accept.mutate()}
          >
            {t('handoff.nudge.accept')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pending}
            onClick={() => decline.mutate()}
          >
            {t('handoff.nudge.decline')}
          </Button>
        </div>
        {failed && (
          <p role="alert" className="text-meta text-status-danger-fg mt-2">
            {t('handoff.nudge.failed')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
